import { VehicleTypePreview } from './VehicleTypePreview';
import { useTranslation } from 'react-i18next';

import type { ProvisionBody } from './useProvisioning';

/**
 * THE ONE DESCRIPTION OF A VEHICLE BEING PROVISIONED (slice 18f).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 EXTRACTED, NOT FORKED — AND THAT IS THE POINT OF THE FILE.
 * ─────────────────────────────────────────────────────────────────────────────
 * There are now TWO entrances to the same server call: the four-step wizard
 * (`ProvisionWizard`, canvas `5g`) and the assignment dialog reached from
 * `/commissioning` and `/devices` (`AssignDeviceDialog`). They collect the same
 * fields for the same `POST /admin/tenants/:id/vehicles`.
 *
 * Copied, they drift. One gains the gt06-only rule and the other does not; one
 * trims the plate and the other sends whitespace; a new column lands in one
 * place and the other keeps sending `null` for it for a year. Every field, its
 * label, its options and the body it becomes live HERE, once, and both screens
 * are layout around them.
 *
 * `Field` and `INPUT` are duplicated from `features/inventory/registerUi` on
 * purpose — importing the register's furniture into the provisioning tree would
 * couple two features that have no other relationship. They are eight lines.
 */

/** The identity of a tracker install, as a form holds it — strings throughout. */
export interface Draft {
  /** A `devices` row uuid — never an IMEI. See `useProvisioning`. */
  deviceId: string;
  plate: string;
  label: string;
  protocol: string;
  model: string;
  vehicleType: string;
  trackerModel: string;
  reportPeriodSec: string;
  commandPassword: string;
  plan: 'track' | 'fleet' | 'pro';
}

export const EMPTY_DRAFT: Draft = {
  deviceId: '',
  plate: '',
  label: '',
  protocol: 'teltonika',
  model: '',
  vehicleType: 'other',
  trackerModel: '',
  reportPeriodSec: '',
  commandPassword: '',
  plan: 'track',
};

export const PROTOCOLS = ['teltonika', 'gt06', 'osmand'] as const;
// 🔴 THIS LIST MUST EQUAL THE DATABASE ENUM, VALUE FOR VALUE.
//
// It did not, and provisioning was broken for anyone who left the field alone:
// the default was 'car', which `vehicle_type` has never accepted. Every attempt
// returned 502 with `invalid input value for enum vehicle_type: "car"`, AFTER the
// Traccar group move had already succeeded -- so a failed assignment left the
// device sitting in the tenant's Traccar group with no vehicle row.
//
// It survived because nothing exercised it: the wizard's own acceptance step
// ("provision ONE device through the wizard") sat unactioned on the waiting list,
// so the first real use was the first execution. `vehicleTypes.test.ts` now reads
// the enum out of the api schema and fails if these two ever drift again -- the
// same by-source technique `inventoryErrors.test.ts` already uses for the
// provisioning error union.
export const VEHICLE_TYPES = [
  'sedan',
  'suv',
  // SLICE 58 — Mahad's Land Cruiser marker (migration 0052).
  'land_cruiser',
  'pickup',
  'van',
  'truck',
  'tanker',
  'bus',
  'motorcycle',
  // 🔴 SLICE 55 — the Bajaj/tuk-tuk three-wheeler. Internal value `three_wheeler`;
  // the label is "Bajaj" (EN) / "Bajaaj" (SO) — Mahad, 18 Sep.
  'three_wheeler',
  'other',
] as const;

export function draftToBody(d: Draft): ProvisionBody {
  const period = d.reportPeriodSec.trim() === '' ? null : Number(d.reportPeriodSec);
  return {
    deviceId: d.deviceId,
    plate: d.plate.trim(),
    label: d.label.trim() || null,
    protocol: d.protocol,
    model: d.model.trim() || null,
    vehicleType: d.vehicleType || null,
    trackerModel: d.trackerModel.trim() || null,
    reportPeriodSec: Number.isFinite(period) && period !== null && period > 0 ? period : null,
    // 🔴 Only gt06 uses one. Sending it for another protocol would store a
    // credential that can never be used and can still leak.
    commandPassword: d.protocol === 'gt06' && d.commandPassword ? d.commandPassword : null,
    plan: d.plan,
  };
}

export const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] text-raad-text outline-none focus:border-raad-primary';

/**
 * 🔴 THE HINT LIVES OUTSIDE THE `<label>`, DELIBERATELY.
 *
 * Nested inside, it becomes part of the control's accessible name — the field
 * for "Report period" is announced as "Report period How often the tracker
 * reports…" and can no longer be found by its own name, by a screen reader or by
 * a test. Caught by a test that could not locate the field it was typing into.
 */
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="block">
      <label className="block">
        <span className="mb-1 block text-[12px] font-medium text-raad-muted">{label}</span>
        {children}
      </label>
      {hint ? <span className="mt-1 block text-[11.5px] text-raad-muted">{hint}</span> : null}
    </div>
  );
}

export type DraftSetter = <K extends keyof Draft>(key: K, value: Draft[K]) => void;

/**
 * Protocol, tracker model, report period — and the gt06 command password.
 *
 * The password field is ABSENT for every other protocol rather than disabled:
 * rendering it invites storing a credential that protocol cannot use, and a
 * stored credential is a credential that can leak.
 */
export function HardwareFields({ draft, set }: { draft: Draft; set: DraftSetter }) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t('provision.protocol')}>
        <select
          className={INPUT}
          value={draft.protocol}
          onChange={(e) => set('protocol', e.target.value)}
        >
          {PROTOCOLS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('provision.trackerModel')}>
        <input
          className={INPUT}
          value={draft.trackerModel}
          onChange={(e) => set('trackerModel', e.target.value)}
          placeholder="fmc920"
        />
      </Field>
      <Field label={t('provision.reportPeriod')} hint={t('provision.reportPeriodHint')}>
        <input
          className={INPUT}
          value={draft.reportPeriodSec}
          onChange={(e) => set('reportPeriodSec', e.target.value)}
          inputMode="numeric"
        />
      </Field>
      {draft.protocol === 'gt06' ? (
        <Field label={t('provision.commandPassword')} hint={t('provision.commandPasswordHint')}>
          <input
            className={INPUT}
            type="password"
            value={draft.commandPassword}
            onChange={(e) => set('commandPassword', e.target.value)}
          />
        </Field>
      ) : null}
    </>
  );
}

/** Plate, label, model, vehicle type — what the customer will read on screen. */
export function IdentityFields({
  draft,
  set,
  autoFocus,
}: {
  draft: Draft;
  set: DraftSetter;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t('provision.plate')}>
        <input
          className={INPUT}
          value={draft.plate}
          onChange={(e) => set('plate', e.target.value)}
          autoFocus={autoFocus === true}
        />
      </Field>
      <Field label={t('provision.label')} hint={t('provision.labelHint')}>
        <input className={INPUT} value={draft.label} onChange={(e) => set('label', e.target.value)} />
      </Field>
      <Field label={t('provision.model')}>
        <input
          className={INPUT}
          value={draft.model}
          onChange={(e) => set('model', e.target.value)}
          placeholder="Toyota Fielder"
        />
      </Field>
      <Field label={t('provision.vehicleType')}>
        {/* Slice 58: the picker shows the map marker the chosen type draws. */}
        <div className="flex items-center gap-3">
          <select
            className={INPUT}
            value={draft.vehicleType}
            onChange={(e) => set('vehicleType', e.target.value)}
          >
            {VEHICLE_TYPES.map((v) => (
              <option key={v} value={v}>
                {t(`provision.type.${v}`)}
              </option>
            ))}
          </select>
          <VehicleTypePreview
            vehicleType={draft.vehicleType}
            label={t(`provision.type.${draft.vehicleType}`)}
          />
        </div>
      </Field>
    </>
  );
}

/**
 * The plan tier.
 *
 * 🔴 THE THREE TIERS ARE `RAAD_MASTER_PLAN.md` §7's, matching the live
 * `plan_tier` enum — NOT canvas `5f`/`6i`'s Bilow / Ganacsi / Shirkad, whose
 * prices this product does not sell (CLAUDE.md, 24 Aug).
 */
export function PlanField({ draft, set }: { draft: Draft; set: DraftSetter }) {
  const { t } = useTranslation();
  return (
    <Field label={t('provision.plan')}>
      <select
        className={INPUT}
        value={draft.plan}
        onChange={(e) => set('plan', e.target.value as Draft['plan'])}
      >
        <option value="track">{t('provision.planTier.track')}</option>
        <option value="fleet">{t('provision.planTier.fleet')}</option>
        <option value="pro">{t('provision.planTier.pro')}</option>
      </select>
    </Field>
  );
}
